'use client';

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { MessageSquare, Send, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMarkMessagesSeen } from '@/components/notification-bell';
import { cn } from '@/lib/utils';
import type { Id } from '../../../../convex/_generated/dataModel';

function formatTime(ts: number) {
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

interface ThreadViewProps {
  parentUserId: Id<'users'>;
  parentName: string;
  onBack: () => void;
}

function ThreadView({ parentUserId, parentName, onBack }: ThreadViewProps) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = useQuery(api.coach.messages.getThread, { parentUserId });
  const sendMsg = useMutation(api.coach.messages.send);
  const markRead = useMutation(api.coach.messages.markRead);

  useEffect(() => {
    markRead({ parentUserId }).catch(() => {});
  }, [parentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await sendMsg({ parentUserId, body });
      setBody('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow border border-border overflow-hidden flex flex-col h-[calc(100vh-12rem)]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/40">
        <button onClick={onBack} className="p-1 hover:bg-muted rounded-lg transition">
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold">
          {parentName.charAt(0).toUpperCase()}
        </div>
        <span className="font-medium text-foreground">{parentName}</span>
      </div>

      <div className="flex flex-col gap-2 p-4 flex-1 overflow-y-auto">
        {messages == null ? (
          <Skeleton className="h-32 w-full" />
        ) : (messages ?? []).length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">No messages yet.</p>
        ) : (
          (messages ?? []).map((msg) => (
            <div
              key={msg.id}
              className={cn('flex', msg.isFromMe ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[75%] px-3 py-2 rounded-2xl text-sm',
                  msg.isFromMe
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'
                )}
              >
                <p>{msg.body}</p>
                <p className={cn('text-xs mt-1', msg.isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                  {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSend} className="flex gap-2 p-3 border-t">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Reply to ${parentName}...`}
          className="flex-1 px-3 py-2 border border-input bg-background text-foreground rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!body.trim() || sending}
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

export default function CoachMessagesPage() {
  useMarkMessagesSeen('coach');

  const [activeThreadParentId, setActiveThreadParentId] = useState<string | null>(null);
  const [activeThreadName, setActiveThreadName] = useState('');

  const threads = useQuery(api.coach.messages.listThreads);

  if (activeThreadParentId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Messages</h1>
        </div>
        <ThreadView
          parentUserId={activeThreadParentId as Id<'users'>}
          parentName={activeThreadName}
          onBack={() => setActiveThreadParentId(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Messages</h1>
          <p className="text-sm text-muted-foreground">Direct messages from parents</p>
        </div>
      </div>

      {/* Parent Threads */}
      {threads === undefined ? (
        <Skeleton className="h-24 w-full" />
      ) : threads === null || threads.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-6 text-center">
          <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No parent messages yet.</p>
          <p className="text-muted-foreground text-xs mt-1">Parents can message you from their portal.</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow divide-y divide-border">
          {threads.map((thread) => (
            <button
              key={thread.parentUserId}
              onClick={() => {
                setActiveThreadParentId(thread.parentUserId);
                setActiveThreadName(thread.displayName);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition text-left"
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                {thread.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={cn('font-medium text-foreground', thread.unreadCount > 0 && 'font-bold')}>
                    {thread.displayName}
                  </span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatTime(thread.lastMessageAt)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground truncate">{thread.lastMessageBody}</p>
              </div>
              {thread.unreadCount > 0 && (
                <span className="flex-shrink-0 w-5 h-5 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center">
                  {thread.unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
